import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Checkbox } from '@mui/material';
import { Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set, over } from '@totalsoft/change-tracking-react';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { isDirty, update } from '@totalsoft/change-tracking';
import { Close, Check } from '@mui/icons-material';
import { StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';


const FinancialProductRulesEditDetailsOutputDataTableRow = ({
  canWrite,
  dirtyInfo,
  dataLens,
  index,
  financialProductRuleOutputDataParamList,
  onCloseEdit,
  onDelete,
  onSave,
  validation,
  listValid
}) => {
  const [t] = useTranslation();
  const data = dataLens |> get;
  const isItemDirty = useMemo(() => dirtyInfo && isDirty(update(['isInEditing', 'isSaving'], false, dirtyInfo)), [dirtyInfo]);

  return (
    <StyledTableRow>
      {data.isInEditing ? (
        <>
          <StyledTableBodyCell>
            <Checkbox
              checked={data.readOnlyYN || false}
              onChange={dataLens.readOnlyYN |> set |> onCheckBoxChange}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Autocomplete
              valueKey={'paramId'}
              labelKey={'paramName'}
              value={{ paramName: data.paramName, paramId: data.paramId }}
              options={financialProductRuleOutputDataParamList}
              onChange={event => {
                over(dataLens, prevValue => ({ ...prevValue, paramName: event.paramName, paramId: event.paramId }));
              }}
              error={!isValid(validation.paramId)}
              helperText={getErrors(validation.paramId)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <TextField
              type='number'
              value={data.defaultValue}
              variant='standard'
              onChange={dataLens.defaultValue |> set}
              error={!isValid(validation.defaultValue)}
              helperText={getErrors(validation.defaultValue)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <TextField
              type='number'
              defaultValue={data.minValue}
              variant='standard'
              onChange={dataLens.minValue |> set}
              error={!isValid(validation.minValue)}
              helperText={getErrors(validation.minValue)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <TextField
              type='number'
              defaultValue={data.maxValue}
              variant='standard'
              onChange={dataLens.maxValue |> set}
              error={!isValid(validation.maxValue)}
              helperText={getErrors(validation.maxValue)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='save'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Save')}
                disabled={!isItemDirty || data.isSaving || !canWrite || !listValid}
                onClick={() => onSave(index)}
              />
              <IconButton
                type='cancel'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.CloseEdit')}
                onClick={() => onCloseEdit(index)}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      ) : (
        <>
          <StyledTableBodyCell>
            <>{(data.readOnlyYN) === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell>{data.paramName}</StyledTableBodyCell>
          <StyledTableBodyCell>{data.defaultValue}</StyledTableBodyCell>
          <StyledTableBodyCell>{data.minValue}</StyledTableBodyCell>
          <StyledTableBodyCell>{data.maxValue}</StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='edit'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Edit')}
                onClick={() => (dataLens.isInEditing |> set)(true)}
                disabled={!canWrite}
              />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(data.financialProductRuleOutputDataId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      )}
    </StyledTableRow>
  );
};

FinancialProductRulesEditDetailsOutputDataTableRow.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  dirtyInfo: PropTypes.object,
  dataLens: PropTypes.object,
  index: PropTypes.number,
  financialProductRuleOutputDataParamList: PropTypes.array,
  onCloseEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  listValid: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsOutputDataTableRow;
